import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, ArrowUpRight, Pencil, Info } from 'lucide-react';
import apiCoreAdapter from '../../../hooks/apiCoreAdapter';
import CircularProgress from '../../common/CircularProgress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { toastUtils } from '../../../hooks/useToast';
import { Calendar } from '@/components/ui/calendar';
import { es } from 'react-day-picker/locale';
import { PROFILE_EDIT_WEBHOOK } from '../../../hooks/api/config';
import {
  VACATION_BLOCK_BRACKETS,
  VACATION_BLOCK_TABLE_HEADERS,
  computeVacationBlockDates,
  formatVacationBlockSentence,
  formatVacationImpactSentence,
  formatVacationDate,
  formatVacationDateShort,
} from '../../../utils/vacationBlockBracket';

/** Subconjunto de DateRange de react-day-picker que necesitamos */
type DateRange = { from?: Date; to?: Date };

interface ProfileVacationsTabProps {
  userEmail: string;
  psychologistId: string;
  onVacationSaved?: () => void;
}

interface VacationPeriod {
  id: string;
  from: string;
  to?: string;
  duration?: number;
  status?: string;
  cancelled?: boolean;
  psychologist?: { email?: string; id?: string };
  blockade?: { id?: string; from: string; to?: string };
}

type VacationStatus = 'Activo' | 'Próximo' | 'Finalizado';

function parseDate(iso: string): Date {
  return new Date(iso.substring(0, 10) + 'T12:00:00');
}

function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Convierte un objeto Date a string YYYY-MM-DD en timezone local. */
function dateToLocalStr(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function formatShort(iso: string, offsetDays = 0): string {
  const d = parseDate(iso);
  if (offsetDays) d.setDate(d.getDate() + offsetDays);
  return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Convierte YYYY-MM-DD → "dd/mm/aaaa" para mostrar en el input de texto.
 */
function isoToDisplay(iso: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

/**
 * Intenta parsear una cadena libre escrita por el usuario (acepta / o -) hacia YYYY-MM-DD.
 * Retorna null si no puede parsear o la fecha no es válida.
 */
function parseUserDate(text: string): string | null {
  const normalized = text.trim().replace(/-/g, '/');
  const parts = normalized.split('/');
  if (parts.length !== 3) return null;
  const [rawD, rawM, rawY] = parts;
  if (!rawD || !rawM || !rawY) return null;
  const y = rawY.padStart(4, '20');
  const m = rawM.padStart(2, '0');
  const d2 = rawD.padStart(2, '0');
  if (y.length !== 4 || Number(y) < 2000) return null;
  const candidate = `${y}-${m}-${d2}`;
  const dt = new Date(candidate + 'T12:00:00');
  if (isNaN(dt.getTime())) return null;
  // verify round-trip to catch invalid combos like 30/02
  if (dateToLocalStr(dt) !== candidate) return null;
  return candidate;
}

function computeEnd(v: VacationPeriod): Date | null {
  if (v.to) return parseDate(v.to);
  if (v.duration) return new Date(parseDate(v.from).getTime() + v.duration * 86400000);
  return null;
}

function diffDays(v: VacationPeriod): number {
  if (v.duration) return v.duration;
  const end = computeEnd(v);
  if (!end) return 0;
  return Math.round((end.getTime() - parseDate(v.from).getTime()) / 86400000);
}

function getStatus(v: VacationPeriod): VacationStatus {
  const now = new Date();
  const start = parseDate(v.from);
  const end = computeEnd(v);
  if (now >= start && (!end || now <= end)) return 'Activo';
  if (now < start) return 'Próximo';
  return 'Finalizado';
}

// Mismo tono naranja que el tag "Vacaciones & días off" del banner de bloqueo (PsychologistProfile.tsx).
const STATUS_STYLES: Record<VacationStatus, string> = {
  Activo:    'bg-orange-100 text-orange-600 border border-orange-300',
  Próximo:   'bg-[#EDE9FA] text-[#6D57BC]',
  Finalizado:'bg-gray-100 text-gray-500',
};

/** Texto mostrado al usuario — separado de la clave interna de estado para no tocar la lógica de `getStatus`. */
const STATUS_LABELS: Record<VacationStatus, string> = {
  Activo:    'En curso',
  Próximo:   'Próximo',
  Finalizado:'Finalizado',
};

/** Texto explicativo del tooltip de cada chip de estado. */
const STATUS_TOOLTIPS: Record<VacationStatus, string> = {
  Próximo:   'Este período todavía no empezó.',
  Activo:    'Estás transitando este período en este momento.',
  Finalizado:'Este período ya terminó.',
};

const PAST_DATE_ERROR = 'La fecha no puede ser anterior a hoy';

export const ProfileVacationsTab: React.FC<ProfileVacationsTabProps> = ({ userEmail, psychologistId, onVacationSaved }) => {
  const navigate = useNavigate();
  const [vacations, setVacations] = useState<VacationPeriod[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal de edición — state canónico en YYYY-MM-DD
  const [editVacation, setEditVacation] = useState<VacationPeriod | null>(null);
  const [editFrom, setEditFrom] = useState('');
  const [editTo, setEditTo] = useState('');
  // Estado de los inputs de texto (dd/mm/aaaa)
  const [editFromText, setEditFromText] = useState('');
  const [editToText, setEditToText] = useState('');
  // Errores de validación
  const [editFromError, setEditFromError] = useState('');
  const [editToError, setEditToError] = useState('');
  const [saving, setSaving] = useState(false);
  const [vacationConsentChecked, setVacationConsentChecked] = useState(false);
  // Paso 1: fechas + consecuencias + checkbox. Paso 2: confirmación con el impacto real antes de guardar.
  const [modalStep, setModalStep] = useState<1 | 2>(1);
  // Mes que muestra el Calendar (controlado para navegar al mes correcto al abrir)
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());

  const fetchVacations = async () => {
    try {
      const res = await apiCoreAdapter.psychologistVacations({ psychologist: psychologistId, skip: 0, take: 100 }) as { results?: VacationPeriod[] } | VacationPeriod[];
      const raw: VacationPeriod[] = Array.isArray(res) ? res : (res as { results?: VacationPeriod[] }).results ?? [];
      const list = raw.filter(v => !v.cancelled);
      list.sort((a, b) => new Date(b.from).getTime() - new Date(a.from).getTime());
      setVacations(list);
    } catch {
      setVacations([]);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchVacations();
      setLoading(false);
    })();
  }, []);

  const hasActive = vacations.some(v => {
    const s = getStatus(v);
    return s === 'Activo' || s === 'Próximo';
  });

  const handleNavigate = () => {
    const qs = new URLSearchParams();
    if (userEmail) qs.set('email', userEmail);
    navigate(`/calendar/time-off?${qs.toString()}`);
  };

  /** Sincroniza todos los estados del modal a partir de un par de valores YYYY-MM-DD.
   *  También navega el Calendar al mes de fromIso (o al mes actual si no hay from). */
  const syncModalState = (fromIso: string, toIso: string) => {
    setEditFrom(fromIso);
    setEditTo(toIso);
    setEditFromText(isoToDisplay(fromIso));
    setEditToText(isoToDisplay(toIso));
    setEditFromError('');
    setEditToError('');
    setCalendarMonth(fromIso ? new Date(fromIso + 'T12:00:00') : new Date());
    setVacationConsentChecked(false);
  };

  const openEdit = (v: VacationPeriod) => {
    const todayStr = localToday();
    setEditVacation(v);
    const fromStr = v.from.substring(0, 10);
    const clampedFrom = fromStr < todayStr ? todayStr : fromStr;
    const end = computeEnd(v);
    const toStr = end ? end.toISOString().substring(0, 10) : '';
    const clampedTo = toStr && toStr < todayStr ? '' : toStr;
    syncModalState(clampedFrom, clampedTo);
    setModalStep(1);
  };

  const closeEdit = () => {
    if (saving) return;
    setEditVacation(null);
    syncModalState('', '');
    setModalStep(1);
  };

  /** Maneja cambio en el input de texto DESDE */
  const handleFromTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setEditFromText(text);
    setEditFromError('');
    const iso = parseUserDate(text);
    if (iso) {
      if (iso < localToday()) {
        setEditFromError(PAST_DATE_ERROR);
        // NO actualiza editFrom: el estado canónico no cambia
      } else {
        setEditFrom(iso);
        // si hay un TO previo y el nuevo FROM lo supera, limpiamos TO
        if (editTo && iso > editTo) {
          setEditTo('');
          setEditToText('');
          setEditToError('');
        }
      }
    }
  };

  const handleFromTextBlur = () => {
    const iso = parseUserDate(editFromText);
    if (editFromText && !iso) {
      // texto incompleto o inválido al salir del campo → restaurar al valor canónico
      setEditFromText(isoToDisplay(editFrom));
      setEditFromError('');
    } else if (iso && iso < localToday()) {
      setEditFromError(PAST_DATE_ERROR);
    }
  };

  /** Maneja cambio en el input de texto HASTA */
  const handleToTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setEditToText(text);
    setEditToError('');
    const iso = parseUserDate(text);
    if (iso) {
      if (iso < localToday()) {
        setEditToError(PAST_DATE_ERROR);
      } else {
        setEditTo(iso);
        setVacationConsentChecked(false);
      }
    }
  };

  const handleToTextBlur = () => {
    const iso = parseUserDate(editToText);
    if (editToText && !iso) {
      setEditToText(isoToDisplay(editTo));
      setEditToError('');
    } else if (iso && iso < localToday()) {
      setEditToError(PAST_DATE_ERROR);
    }
  };

  /** Cuando el Calendar de rango selecciona un rango, sincroniza todo.
   *  Doble guardia: aunque react-day-picker marque la fecha como disabled,
   *  rechazamos cualquier fecha pasada a nivel de handler. */
  const handleCalendarSelect = (range: DateRange | undefined) => {
    const todayStr = localToday();
    const newFrom = range?.from ? dateToLocalStr(range.from) : '';
    const newTo   = range?.to   ? dateToLocalStr(range.to)   : '';
    // Si la fecha propuesta es anterior a hoy, la descartamos completamente
    if (newFrom && newFrom < todayStr) return;
    if (newTo   && newTo   < todayStr) return;
    syncModalState(newFrom, newTo);
  };

  const handleSave = async () => {
    if (!editVacation || !editFrom) return;
    if (editFromError || editToError) return;
    setSaving(true);
    try {
      const body = {
        from: editFrom + 'T00:00:00.000Z',
        to: editTo ? editTo + 'T00:00:00.000Z' : undefined,
        psychologist_id: psychologistId,
        vacations: editVacation.id,
      };
      const res = await fetch(PROFILE_EDIT_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Error al guardar');
      toastUtils.success('Vacaciones actualizadas correctamente');

      // Email de consentimiento a la psicóloga
      const consentNow = new Date();
      const fechaConsentimiento =
        consentNow.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) +
        ' a las ' +
        consentNow.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
      const fromDateObj = new Date(editFrom + 'T12:00:00');
      const toDateObj = editTo ? new Date(editTo + 'T12:00:00') : null;
      const blockResult = toDateObj ? computeVacationBlockDates(fromDateObj, toDateObj) : null;
      const emailDays = blockResult ? blockResult.days : null;
      const fromDisplay = formatShort(editFrom);
      const toDisplay = editTo ? formatShort(editTo) : 'sin fecha de fin';
      const emailActiveRow = blockResult ? VACATION_BLOCK_BRACKETS.indexOf(blockResult.bracket) : null;
      const detailSentence = toDateObj ? formatVacationBlockSentence(fromDateObj, toDateObj, formatVacationDate) : null;
      const vacTableRows = VACATION_BLOCK_BRACKETS.map((bracket, i) => {
        const isActive = emailActiveRow === i;
        const bg = isActive ? 'background-color:#F0EEFB;' : '';
        const color = isActive ? 'color:#5a47a8;font-weight:600;' : 'color:#374151;';
        return `<tr style="${bg}">
          <td style="padding:10px 14px;font-size:13px;${color}border-bottom:1px solid #e5e7eb;">${bracket.label}${isActive ? ' ◀' : ''}</td>
          <td style="padding:10px 14px;font-size:13px;${color}border-bottom:1px solid #e5e7eb;">${bracket.blockBeforeLabel}</td>
          <td style="padding:10px 14px;font-size:13px;${color}border-bottom:1px solid #e5e7eb;">${bracket.blockAfterLabel}</td>
        </tr>`;
      }).join('');
      const vacConsentHtml = `<!DOCTYPE html>
<html lang="es">
<head><meta charset="UTF-8" /><meta name="viewport" content="width=device-width, initial-scale=1.0" /></head>
<body style="margin:0;padding:0;background-color:#f0f0f0;font-family:Arial,Helvetica,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f0f0f0;padding:32px 0;">
    <tr><td align="center">
      <table width="560" cellpadding="0" cellspacing="0" border="0" style="background-color:#ffffff;border-radius:12px;overflow:hidden;max-width:560px;width:100%;">
        <tr>
          <td align="center" style="background-color:#4C1D95;padding:28px 32px;">
            <p style="margin:0;color:#ffffff;font-size:20px;font-weight:700;letter-spacing:0.3px;font-family:Arial,Helvetica,sans-serif;">
              Modificación de vacaciones registrada ✅
            </p>
          </td>
        </tr>
        <tr>
          <td style="padding:32px 36px 8px;">
            <p style="margin:0 0 12px;font-size:15px;color:#1a1f36;font-family:Arial,Helvetica,sans-serif;">Hola,</p>
            <p style="margin:0 0 24px;font-size:15px;color:#444444;line-height:1.6;font-family:Arial,Helvetica,sans-serif;">
              Te enviamos este mail para dejar registro de que el <strong>${fechaConsentimiento}</strong>
              confirmaste la modificación de tu período de vacaciones en Psi Platform.
            </p>
            <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#F5F3FF;border-left:4px solid #7C3AED;border-radius:0 8px 8px 0;margin-bottom:24px;">
              <tr><td style="padding:16px 20px;">
                <p style="margin:0 0 8px;font-size:12px;font-weight:700;color:#7C3AED;text-transform:uppercase;letter-spacing:0.8px;font-family:Arial,Helvetica,sans-serif;">Período solicitado</p>
                <p style="margin:0;font-size:15px;color:#1a1f36;font-family:Arial,Helvetica,sans-serif;">
                  <strong>${fromDisplay}</strong> → <strong>${toDisplay}</strong>${emailDays !== null ? ` (${emailDays} ${emailDays === 1 ? 'día' : 'días'})` : ''}
                </p>
              </td></tr>
            </table>
            <p style="margin:0 0 12px;font-size:12px;font-weight:700;color:#7C3AED;text-transform:uppercase;letter-spacing:0.8px;font-family:Arial,Helvetica,sans-serif;">Consecuencias según duración</p>
            <div style="overflow-x:auto;border-radius:10px;border:1px solid #e5e7eb;margin-bottom:24px;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr style="background-color:#f9fafb;">
                  <th style="padding:10px 14px;font-size:12px;font-weight:700;color:#374151;text-align:left;border-bottom:1px solid #e5e7eb;">${VACATION_BLOCK_TABLE_HEADERS.days}</th>
                  <th style="padding:10px 14px;font-size:12px;font-weight:700;color:#374151;text-align:left;border-bottom:1px solid #e5e7eb;">${VACATION_BLOCK_TABLE_HEADERS.before}</th>
                  <th style="padding:10px 14px;font-size:12px;font-weight:700;color:#374151;text-align:left;border-bottom:1px solid #e5e7eb;">${VACATION_BLOCK_TABLE_HEADERS.after}</th>
                </tr>
                ${vacTableRows}
              </table>
            </div>
            ${detailSentence ? `<p style="margin:0 0 24px;font-size:13px;color:#7C3AED;font-family:Arial,Helvetica,sans-serif;line-height:1.5;">▶ ${detailSentence}</p>` : ''}
            <p style="margin:0 0 24px;font-size:14px;color:#555555;line-height:1.6;font-family:Arial,Helvetica,sans-serif;">
              Guardá este mail como constancia. Si tenés alguna duda escribinos a
              <a href="mailto:psicos@psimammoliti.com" style="color:#7C3AED;font-weight:700;text-decoration:none;">psicos@psimammoliti.com</a>.
            </p>
          </td>
        </tr>
        <tr><td style="padding:8px 36px 28px;"><p style="margin:0;font-size:13px;font-style:italic;color:#1a1f36;font-weight:700;font-family:Arial,Helvetica,sans-serif;">Equipo Psi Mammoliti</p></td></tr>
        <tr><td style="padding:0 36px 16px;"><p style="margin:0;font-size:12px;color:#888888;font-family:Arial,Helvetica,sans-serif;line-height:1.5;">Por favor no respondas este mensaje, ya que es automático. Cualquier inquietud podés contactarte a <a href="mailto:psicos@psimammoliti.com" style="color:#888888;text-decoration:underline;">psicos@psimammoliti.com</a>.</p></td></tr>
        <tr><td style="padding:0 36px 24px;"><hr style="border:none;border-top:1px solid #e8e8e8;margin:0 0 16px;" /><p style="margin:0;font-size:11px;color:#aaaaaa;text-align:center;font-family:Arial,Helvetica,sans-serif;"><a href="#" style="color:#aaaaaa;text-decoration:underline;">Click here to unsubscribe</a></p></td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
      void apiCoreAdapter.psychologistNotifyProfileChanges({
        html: vacConsentHtml,
        subject: 'Confirmación de modificación de vacaciones',
        from: 'no-responder@psimammoliti.com',
      });

      closeEdit();
      await fetchVacations();
      onVacationSaved?.();
    } catch {
      toastUtils.error('No se pudo guardar. Intentá de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h3 className="section-title">Vacaciones y Días off</h3>

      {/* Botón principal */}
      <button
        type="button"
        onClick={handleNavigate}
        className="inline-flex items-center gap-2 border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors mb-5"
      >
        <CalendarDays size={15} className="text-gray-500" />
        Informar vacaciones &amp; días off
        <ArrowUpRight size={14} className="text-gray-400" />
      </button>

      {/* Banner informativo si hay período activo/próximo */}
      {hasActive && (
        <div className="flex items-start gap-3 bg-[#F0EEFB] border border-[#D6CFF5] rounded-xl px-4 py-3 mb-5 text-sm text-[#5a47a8]">
          <Info size={16} className="mt-0.5 flex-shrink-0 text-[#6D57BC]" />
          <p className="m-0 leading-snug">
            Estás transitando un período de días off o entrarás en uno en el corto plazo. Esto limita la asignación de nuevos pacientes.
          </p>
        </div>
      )}

      {/* Lista */}
      <p className="text-sm font-semibold text-gray-700 pb-5">Detalle vacaciones &amp; días off</p>

      {loading ? (
        <div className="flex justify-center py-8">
          <CircularProgress sizeRem={1.75} color="#6D57BC" label="Cargando..." />
        </div>
      ) : vacations.length === 0 ? (
        <p className="text-sm text-gray-400 italic py-4">No hay períodos registrados.</p>
      ) : (
        <>
          <div className="flex flex-col divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
            {vacations.map((v) => {
              const status = getStatus(v);
              const days = diffDays(v);
              const endDate = computeEnd(v);
              return (
                <div key={v.id} className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3 px-4 py-3 bg-white hover:bg-gray-50 transition-colors">
                  {/* Icono + Fechas */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#EDE9FA] flex items-center justify-center">
                      <CalendarDays size={16} className="text-[#6D57BC]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 break-words">
                        {formatShort(v.from)}{endDate ? ` → ${formatShort(endDate.toISOString())}` : ''}
                      </p>
                      <p className="text-xs text-gray-400">{days} {days === 1 ? 'día' : 'días'}</p>
                    </div>
                  </div>

                  {/* Badge + acción — indented on mobile to align under text */}
                  <div className="flex items-center gap-2 pl-12 sm:pl-0 sm:flex-shrink-0">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_STYLES[status]}`}>
                          {STATUS_LABELS[status]}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{STATUS_TOOLTIPS[status]}</TooltipContent>
                    </Tooltip>
                    {(() => {
                      const isFinalized = status === 'Finalizado';
                      const modificarButton = (
                        <button
                          type="button"
                          onClick={() => { if (!isFinalized) openEdit(v); }}
                          aria-disabled={isFinalized}
                          className={`inline-flex items-center gap-1 text-xs font-medium ${
                            isFinalized ? 'text-gray-400 cursor-not-allowed' : 'text-[#6D57BC] hover:underline cursor-pointer'
                          }`}
                        >
                          <Pencil size={12} />
                          Modificar
                        </button>
                      );
                      if (!isFinalized) return modificarButton;
                      return (
                        <Tooltip>
                          <TooltipTrigger asChild>{modificarButton}</TooltipTrigger>
                          <TooltipContent>No se pueden modificar períodos finalizados.</TooltipContent>
                        </Tooltip>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-gray-400 text-right pt-4">
            {vacations.length} {vacations.length === 1 ? 'registro' : 'registros'}
          </p>
        </>
      )}

      {/* Modal de edición */}
      <Dialog open={!!editVacation} onOpenChange={(open) => { if (!open) closeEdit(); }}>
        <DialogContent className="sm:max-w-2xl w-full p-0 overflow-hidden">
          {modalStep === 1 && (
          <div className="px-6 pt-6 pb-2 max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-base">Modificar período</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-500 mt-1 mb-5">
              Editá las fechas de inicio y fin de tu período.
            </p>

            {/* Inputs de texto para tipear fechas */}
            <div className="flex gap-3 mb-1">
              {/* DESDE */}
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Desde
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="dd/mm/aaaa"
                  value={editFromText}
                  onChange={handleFromTextChange}
                  onBlur={handleFromTextBlur}
                  aria-label="Fecha desde"
                  aria-invalid={!!editFromError}
                  className={[
                    'w-full text-sm px-3 py-2.5 rounded-lg border focus:outline-none focus:ring-2 transition-colors',
                    editFromError
                      ? 'border-red-400 bg-red-50 text-red-700 focus:ring-red-200 focus:border-red-500'
                      : 'border-gray-200 bg-white text-gray-800 focus:ring-[#6D57BC]/25 focus:border-[#6D57BC]',
                  ].join(' ')}
                />
                {editFromError && (
                  <p className="text-xs text-red-600 mt-0.5" role="alert">
                    {editFromError}
                  </p>
                )}
              </div>

              {/* HASTA */}
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Hasta
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="dd/mm/aaaa"
                  value={editToText}
                  onChange={handleToTextChange}
                  onBlur={handleToTextBlur}
                  aria-label="Fecha hasta"
                  aria-invalid={!!editToError}
                  className={[
                    'w-full text-sm px-3 py-2.5 rounded-lg border focus:outline-none focus:ring-2 transition-colors',
                    editToError
                      ? 'border-red-400 bg-red-50 text-red-700 focus:ring-red-200 focus:border-red-500'
                      : 'border-gray-200 bg-white text-gray-800 focus:ring-[#6D57BC]/25 focus:border-[#6D57BC]',
                  ].join(' ')}
                />
                {editToError && (
                  <p className="text-xs text-red-600 mt-0.5" role="alert">
                    {editToError}
                  </p>
                )}
              </div>
            </div>

            {/* Calendar de rango — disabled bloquea fechas pasadas visualmente y al click.
                defaultMonth navega al mes de editFrom al abrir el modal.
                pointer-events-none en disabled + guard en onSelect = doble protección. */}
            <div className="flex justify-center rounded-xl border border-gray-200 p-2">
              <Calendar
                mode="range"
                locale={es}
                month={calendarMonth}
                onMonthChange={setCalendarMonth}
                selected={{
                  from: editFrom ? new Date(editFrom + 'T12:00:00') : undefined,
                  to:   editTo   ? new Date(editTo   + 'T12:00:00') : undefined,
                }}
                onSelect={handleCalendarSelect}
                disabled={(date: Date) => dateToLocalStr(date) < localToday()}
                classNames={{
                  disabled: 'text-muted-foreground opacity-30 pointer-events-none cursor-default',
                  caption_label: 'text-sm font-medium capitalize',
                }}
              />
            </div>

            {/* Consecuencias + checkbox */}
            {(() => {
              const fromDate = editFrom ? new Date(editFrom + 'T12:00:00') : null;
              const toDate = editTo ? new Date(editTo + 'T12:00:00') : null;
              const blockResult = (fromDate && toDate) ? computeVacationBlockDates(fromDate, toDate) : null;
              const activeRow = blockResult ? VACATION_BLOCK_BRACKETS.indexOf(blockResult.bracket) : null;
              const detailSentence = (fromDate && toDate) ? formatVacationBlockSentence(fromDate, toDate, formatVacationDateShort) : null;
              return (
                <div className="mt-4 flex flex-col gap-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider m-0">
                    Consecuencias de tu solicitud
                  </p>
                  <div className="overflow-x-auto rounded-xl border border-gray-200">
                    <table className="w-full text-xs table-fixed">
                      <colgroup>
                        <col className="w-[20%]" />
                        <col className="w-[34%]" />
                        <col className="w-[46%]" />
                      </colgroup>
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left font-semibold text-gray-600 px-3 py-2 border-b border-r border-gray-200">{VACATION_BLOCK_TABLE_HEADERS.days}</th>
                          <th className="text-left font-semibold text-gray-600 px-3 py-2 border-b border-r border-gray-200">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="border-b border-dotted border-gray-400 cursor-help">
                                  {VACATION_BLOCK_TABLE_HEADERS.before}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="text-center">
                                Durante este período no se te asignarán pacientes nuevos. No afecta la atención de tus pacientes activos, que seguís atendiendo con normalidad.
                              </TooltipContent>
                            </Tooltip>
                          </th>
                          <th className="text-left font-semibold text-gray-600 px-3 py-2 border-b border-gray-200">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="border-b border-dotted border-gray-400 cursor-help">
                                  {VACATION_BLOCK_TABLE_HEADERS.after}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="text-center">
                                Indica si tu agenda vuelve a recibir pacientes nuevos apenas regresás, o si el bloqueo continúa un tiempo más después de tu regreso, según la duración de tu ausencia.
                              </TooltipContent>
                            </Tooltip>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {VACATION_BLOCK_BRACKETS.map((bracket, i) => {
                          const isActive = activeRow === i;
                          return (
                            <tr key={bracket.key} className={isActive ? 'bg-[#F0EEFB]' : undefined}>
                              <td className={`px-3 py-2 leading-snug border-r border-gray-200 ${isActive ? 'text-[#5a47a8] font-semibold' : 'text-gray-700'}`}>
                                <div className="flex items-start gap-1.5">
                                  {isActive && <span className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#6D57BC] inline-block" />}
                                  <span>{bracket.label}</span>
                                </div>
                              </td>
                              <td className={`px-3 py-2 leading-snug border-r border-gray-200 ${isActive ? 'text-[#5a47a8] font-medium' : 'text-gray-600'}`}>{bracket.blockBeforeLabel}</td>
                              <td className={`px-3 py-2 leading-snug ${isActive ? 'text-[#5a47a8] font-medium' : 'text-gray-600'}`}>{bracket.blockAfterLabel}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {detailSentence && (
                    <p className="text-xs text-[#6D57BC] m-0 flex items-start gap-1.5">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#6D57BC] flex-shrink-0 inline-block" />
                      <span>{detailSentence}</span>
                    </p>
                  )}
                  <label htmlFor="vacation-consent-checkbox" className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      id="vacation-consent-checkbox"
                      type="checkbox"
                      checked={vacationConsentChecked}
                      onChange={(e) => setVacationConsentChecked(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 accent-purple-600 cursor-pointer flex-shrink-0"
                    />
                    <span className="text-sm text-gray-700">Estoy de acuerdo con lo informado arriba</span>
                  </label>
                </div>
              );
            })()}
          </div>
          )}

          {modalStep === 1 && (
          <div className="flex justify-end gap-2 px-6 py-5 border-t border-gray-100">
            <Button variant="outline" onClick={closeEdit} disabled={saving}>
              Cancelar
            </Button>
            <Button
              onClick={() => setModalStep(2)}
              disabled={!editFrom || !!editFromError || !!editToError || !vacationConsentChecked}
              className="bg-[#6D57BC] text-white hover:bg-[#5a47a8]"
            >
              Continuar
            </Button>
          </div>
          )}

          {modalStep === 2 && (() => {
            const fromDate = editFrom ? new Date(editFrom + 'T12:00:00') : null;
            const toDate = editTo ? new Date(editTo + 'T12:00:00') : null;
            const impactSentence = (fromDate && toDate) ? formatVacationImpactSentence(fromDate, toDate, formatVacationDate) : null;
            return (
              <>
                <div className="px-6 pt-6 pb-4">
                  <DialogHeader>
                    <DialogTitle className="text-base">Confirmá el cambio</DialogTitle>
                  </DialogHeader>
                  <p className="text-sm text-gray-500 mt-1 mb-4">
                    Este es el impacto real en tu agenda con las fechas que elegiste.
                  </p>
                  <div className="rounded-2xl bg-[#F0EEFB] border border-[#D6CFF5] px-4 py-4">
                    <p className="text-sm text-[#5a47a8] font-medium m-0 leading-relaxed">
                      {impactSentence}
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-2 px-6 py-5 border-t border-gray-100">
                  <Button variant="outline" onClick={() => setModalStep(1)} disabled={saving}>
                    Atrás
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-[#6D57BC] text-white hover:bg-[#5a47a8]"
                  >
                    {saving ? 'Guardando...' : 'Guardar cambios'}
                  </Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfileVacationsTab;
